import React, { ReactElement, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery } from 'react-query';

import { AnyObject } from 'immer/dist/internal';

import { DEFAULT_SEARCH_QUANTITY, SearchType } from '@type/web/event';

import useRouterHook from '@lib/hook/useRouterHook';

import { getEventList } from '@api/event/event';

export interface UseInfiniteQueryWithScrollParamsTypes {
  currentSearchType: SearchType;
  queryString: AnyObject;
}

interface UseInfiniteQueryWithScrollReturnTypes {
  data: any[] | undefined;
  error: string | undefined | unknown;
  isFetching: boolean;
  ObservationComponent: () => ReactElement;
}

const isSearchType = (target: string | undefined): boolean => {
  if (!target) return false;
  return target in SearchType;
};

/**
 * 사용 기술
 * Recat query: useInfiniteQuery (https://react-query.tanstack.com/guides/infinite-queries)
 * react-intersection-observer: useInView
 */
export default function useInfiniteQueryWithScroll(): UseInfiniteQueryWithScrollReturnTypes {
  const { queryString } = useRouterHook();
  const searchType = (queryString?.searchType as SearchType) || SearchType.ING;
  const [currentSearchType, setCurrentSearchType] = useState(SearchType.ING);

  useEffect(() => {
    setCurrentSearchType(
      isSearchType(searchType) ? searchType : SearchType.ING,
    );
  }, [searchType]);

  const getEventListWithPageInfo = async ({ pageParam = 0 }) => {
    const { data } = await getEventList({
      ...queryString,
      eventType: currentSearchType,
      searchType: currentSearchType,
      startIndex: pageParam,
    });

    const nextPage =
      data.length >= DEFAULT_SEARCH_QUANTITY ? pageParam + 1 : undefined;

    return {
      result: data,
      nextPage,
      isLast: !nextPage,
    };
  };

  const { data, error, isFetching, fetchNextPage } = useInfiniteQuery(
    ['eventListData', currentSearchType],
    getEventListWithPageInfo,
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
    },
  );

  const ObservationComponent = (): ReactElement => {
    const [ref, inView] = useInView();

    useEffect(() => {
      if (!data) return;

      const pageLastIdx = data.pages.length - 1;
      const isLast = data?.pages[pageLastIdx].isLast;

      if (!isLast && inView) fetchNextPage();
    }, [inView]);

    return <div ref={ref} />;
  };

  return {
    data: data?.pages,
    error,
    isFetching,
    ObservationComponent,
  };
}
